import styles from './Auth.module.css';
import SubmitButton from './SubmitButton';
import DropDownMenu from './DropDownMenu';
import { roles, researcher, university_admin } from './roles';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import ImageUpload from '../../components/ImageUpload';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../../firebase';

const AccountDetails = ({ user, userFromDb }) => {
  const [role, setRole] = useState(userFromDb?.role || '');
  const [subRole, setSubRole] = useState(userFromDb?.subRole || '');
  const [roleDesc, setRoleDesc] = useState(userFromDb?.roleDescription || '');
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (user?.displayName) {
      const { firstName, middleName, lastName } = splitName(user.displayName);
      setFirstName(firstName);
      setMiddleName(middleName);
      setLastName(lastName);
    }
  }, [user]);

  useEffect(() => {
    setRole(userFromDb.role || '');
    setSubRole(userFromDb.subRole || '');
    setRoleDesc(userFromDb.roleDescription || '');
  }, [userFromDb]);

  let subRoles = [];

  switch (role) {
    case 'Researcher':
      subRoles = researcher;
      break;
    case 'University Administration':
      subRoles = university_admin;
      break;
  }

  function splitName(fullName) {
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    const middleName = nameParts.slice(1, -1).join(' ');

    return {
      firstName,
      middleName,
      lastName,
    };
  }

  const submitForm = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    const url = process.env.REACT_APP_FLASK_WEBSERVER + 'edit_user';
    const newName = firstName + ' ' + middleName + ' ' + lastName;

    let data = {};
    if (user.displayName !== newName) {
      data['display_name'] = newName;
    }
    data['role'] = role;
    data['subRole'] = subRole;

    if (selectedFile) {
      const storageRef = ref(storage, selectedFile.name);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      try {
        // Wait for upload to finish and get the download URL
        const downloadURL = await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log('Upload is ' + progress + '% done');
            },
            (error) => {
              console.error('Upload failed', error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        // Add the image URL to the newItem object
        data['image_url'] = downloadURL;
        console.log('File uploaded successfully:', downloadURL);
      } catch (error) {
        console.error('Error uploading file:', error);
        setSubmitting(false);
        return;
      }
    } else if (selectedFile === '') {
      data['image_url'] = '';
    }
    if (role === 'Other') data['roleDescription'] = roleDesc;
    else data['roleDescription'] = '';

    if (Object.keys(data).length !== 0) {
      const token = await user.getIdToken();
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (res.status === 200) {
        toast.success('Profile updated successfully!', {
          className: 'toast',
          autoClose: 3000,
        });
      } else {
        toast.error('Unable to update profile', {
          className: 'toast',
          autoClose: 3000,
        });
      }
    }

    if (role !== 'Other') setRoleDesc('');
    setSubmitting(false);
  };

  const changeRole = (role) => {
    setRole(role);
    setSubRole('');
  };

  const changeSubRole = (role) => {
    setSubRole(role);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  const changeRoleDesc = (desc) => {
    setRoleDesc(desc);
  };

  return (
    <form
      onSubmit={submitForm}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        textAlign: 'left',
        width: '400px',
      }}
    >
      <div>
        <label className={styles['input-label']}>
          First Name&nbsp;<span style={{ color: 'red' }}>*</span>
        </label>
        <input
          className={styles['input-text']}
          type="text"
          id="firstname"
          name="firstname"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </div>
      <div>
        <label className={styles['input-label']}>Middle Name</label>
        <input
          className={styles['input-text']}
          type="text"
          id="middlename"
          name="middlename"
          value={middleName}
          onChange={(e) => setMiddleName(e.target.value)}
        />
      </div>
      <div>
        <label className={styles['input-label']}>
          Last Name &nbsp;<span style={{ color: 'red' }}>*</span>
        </label>
        <input
          className={styles['input-text']}
          type="text"
          id="lastname"
          name="lastname"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </div>
      <div>
        <label className={styles['input-label']}>Email</label>
        <input
          className={styles['input-text']}
          type="text"
          id="email"
          name="email"
          disabled
          value={user.email}
        />
      </div>

      <div>
        <DropDownMenu
          label="Role"
          activeItem={role}
          items={roles}
          changeSelection={changeRole}
          placeholder="Select your role"
        />
      </div>

      {(role === 'Researcher' || role === 'University Administration') && (
        <div>
          <DropDownMenu
            label="Sub Role"
            activeItem={subRole}
            items={subRoles}
            changeSelection={changeSubRole}
            placeholder="Select your sub role"
          />
        </div>
      )}

      {role === 'Other' && (
        <div>
          <label className={styles['input-label']}>Role Description</label>{' '}
          <textarea
            className="input-text"
            name="roleDescription"
            rows={8}
            value={roleDesc}
            onChange={(e) => changeRoleDesc(e.target.value)}
          />
        </div>
      )}

      <ImageUpload
        existingImageUrl={userFromDb?.image_url}
        onFileSelect={handleFileSelect}
        previewShape={'circle'}
      />

      <div style={{ paddingTop: '20px' }}>
        <SubmitButton
          text="Save Changes"
          disabled={submitting}
          width="100%"
          height="40px"
          fontSize="14px"
          fontWeight="400"
        />
      </div>
    </form>
  );
};
export default AccountDetails;
